import type { CSSProperties } from "react";
import type { AdTokenNote } from "./adTokenScan";
import { adTokenSeverity, type AdTokenSeverity } from "./adTokenSeverity";

/**
 * What the ad token check found, as text (MPN-1930).
 *
 * Plain elements with inline styles and nothing else -- no MUI, no stylesheet -- so it sits
 * as easily in Create Studio's inline-styled properties panel (MPN-1933) as beside an ad copy
 * field. `className`/`style` go on the wrapper, `noteStyle` on each note, and every note says
 * what it is in words, so nothing depends on telling red from amber.
 */

export const AD_TOKEN_CHECKS_UNAVAILABLE = "Ad token checks unavailable";

const COLORS: Record<AdTokenSeverity, string> = {
    warning: "#dc2626",
    info: "#475569",
    todo: "#8f5405",
};

/** A note's color, by what it asks of the writer. */
export const adTokenNoteColor = (note: Pick<AdTokenNote, "kind">) => COLORS[adTokenSeverity(note.kind)];

export default function AdTokenMessages({
    notes,
    unavailable = false,
    onUseCanonical,
    className,
    style,
    noteStyle,
}: {
    notes: AdTokenNote[];
    /** Show the one neutral line saying there was nothing to check against. */
    unavailable?: boolean;
    /** Offer "Use [TERM]" on an alias note. Without it the note is information only. */
    onUseCanonical?: (note: AdTokenNote) => void;
    className?: string;
    style?: CSSProperties;
    noteStyle?: CSSProperties;
}) {
    if (!notes.length && !unavailable) return null;

    return (
        <span className={className} style={style}>
            {unavailable && (
                <span data-kind="unavailable" style={{ color: "#64748b", ...noteStyle }}>
                    {AD_TOKEN_CHECKS_UNAVAILABLE}
                </span>
            )}
            {notes.map((note) => (
                <span key={note.normalized} data-kind={note.kind} style={{ color: adTokenNoteColor(note), ...noteStyle }}>
                    {note.kind === "instruction" ? (
                        // #1315's treatment, so the field and the picker say it the same way.
                        <>
                            {note.raw}{" "}
                            <span style={{
                                padding: "0 5px", borderRadius: 4, background: "#fdf3e3",
                                fontSize: 10, fontWeight: 600, whiteSpace: "nowrap",
                            }}>
                                you fill this in
                            </span>
                        </>
                    ) : (
                        note.message
                    )}
                    {/* Information, not a warning: an alias resolves. The canonical is offered
                        because it is the spelling the registry is curated around. */}
                    {note.kind === "alias" && onUseCanonical && (
                        <>
                            {" "}
                            <button
                                type="button"
                                // Keep focus where it is: blurring the field on the way would
                                // save the old spelling a moment before the new one lands.
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => onUseCanonical(note)}
                                style={{
                                    border: 0, padding: 0, background: "none", cursor: "pointer",
                                    font: "inherit", color: "#0a524d", fontWeight: 600,
                                    textDecoration: "underline",
                                }}
                            >
                                Use {note.canonical}
                            </button>
                        </>
                    )}
                </span>
            ))}
        </span>
    );
}
